/**
 * @file AnimatedCard.tsx
 * @description Reusable animated card component with support for staggered animations
 *
 * @cursorRules
 * - animation-guidelines.mdc - Animation principles
 * - modern-component-patterns.mdc - Next.js component patterns
 */
"use client"

import { ReactNode } from "react"
import { motion, Variants } from "framer-motion"

// Create motion components
const MotionDiv = motion.create("div")

interface AnimatedCardProps {
  children: ReactNode
  className?: string
  /** Index for staggered animations */
  index: number
  /** Delay multiplier for staggered animations (default 0.1) */
  staggerDelay?: number
  /** Custom animation variants */
  customVariants?: Variants
  /** Whether to repeat animation on scroll (default false) */
  repeatOnScroll?: boolean
}

export const AnimatedCard = ({
  children,
  className = "",
  index,
  staggerDelay = 0.1,
  customVariants,
  repeatOnScroll = false,
}: AnimatedCardProps) => {
  // Default card animation variants - more dramatic
  const defaultVariants: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: staggerDelay * i,
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  }

  // Use custom variants if provided, otherwise use default
  const variants = customVariants || defaultVariants

  return (
    <MotionDiv
      className={className}
      custom={index}
      variants={variants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: !repeatOnScroll, amount: 0.3 }}
    >
      {children}
    </MotionDiv>
  )
}
